<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import EasyMDE from 'easymde'
import 'easymde/dist/easymde.min.css'
import { renderMarkdown } from '@/markdown'

const props = defineProps<{
  modelValue: string
  visible?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const textareaRef = ref<HTMLTextAreaElement | null>(null)
let editor: EasyMDE | null = null

const underlineButton: EasyMDE.ToolbarIcon = {
  name: 'underline',
  action: (ed) => {
    const cm = (ed as any).codemirror
    const selection = cm.getSelection()
    const replacement = `++${selection || 'text'}++`
    cm.replaceSelection(replacement)
    if (!selection) {
      const cursor = cm.getCursor()
      cm.setCursor({ line: cursor.line, ch: cursor.ch - 4 })
    }
  },
  className: 'custom-underline',
  title: 'Underline',
}

const inlineCodeButton: EasyMDE.ToolbarIcon = {
  name: 'inline-code',
  action: (ed) => {
    const cm = (ed as any).codemirror
    const selection = cm.getSelection()
    const replacement = `\`${selection || 'code'}\``
    cm.replaceSelection(replacement)
    if (!selection) {
      const cursor = cm.getCursor()
      cm.setCursor({ line: cursor.line, ch: cursor.ch - 5 })
    }
  },
  className: 'custom-inline-code',
  title: 'Inline code',
  icon: '&lt;/&gt;',
}

function createEditor() {
  if (editor || !textareaRef.value) return

  editor = new EasyMDE({
    element: textareaRef.value,
    spellChecker: false,
    autofocus: false,
    status: false,
    minHeight: '200px',
    toolbar: [
      'bold', 'italic', 'strikethrough',
      underlineButton, inlineCodeButton, 'code', 'link',
      '|', 'unordered-list', 'ordered-list',
      '|', 'preview', 'guide',
    ],
    previewRender: (plainText: string) => renderMarkdown(plainText),
    promptURLs: true,
    renderingConfig: { singleLineBreaks: false },
    initialValue: props.modelValue,
  })

  editor.codemirror.on('change', () => {
    emit('update:modelValue', editor!.value())
  })
}

function destroyEditor() {
  if (!editor) return
  editor.toTextArea()
  editor = null
}

// Sync external value changes into the editor (e.g. when switching entries)
watch(
  () => props.modelValue,
  (newVal) => {
    if (editor && editor.value() !== newVal) {
      editor.value(newVal)
    }
  }
)

// Recreate editor when the tab becomes visible (v-show toggling)
watch(
  () => props.visible,
  async (vis) => {
    if (vis) {
      await nextTick()
      if (!editor) {
        createEditor()
      } else {
        // Refresh CodeMirror when tab becomes visible (fixes layout)
        editor.codemirror.refresh()
      }
    }
  }
)

onMounted(() => {
  // Only create if visible (or visibility not controlled)
  if (props.visible !== false) {
    createEditor()
  }
})

onBeforeUnmount(() => {
  destroyEditor()
})
</script>

<template>
  <textarea ref="textareaRef"></textarea>
</template>
